// ═══════════════════════════════════════════════════════════════
// Component Registry - Central Configuration
// ═══════════════════════════════════════════════════════════════
import { ComponentType } from "react";

import { ComponentRegistryEntry, EditorField, ADVANCED_SECTION_FIELDS } from "@/config/ecoBuilder/types";
// Basic & Layout
import { Container } from "@/components/ecoBuilder/layout/Container";
import { TextAtom, TextAtomDefaultProps, TextAtomSchema } from "@/components/ecoBuilder/atoms/TextAtom";
import { ButtonAtom, ButtonAtomDefaultProps, ButtonAtomSchema } from "@/components/ecoBuilder/atoms/ButtonAtom";

// Eco Blocks
import { EcoPageContent } from "@/components/ecoBuilder/blocks/eco/EcoPageContent";
import { StandardHeader, StandardHeaderDefaultProps, StandardHeaderSchema } from "@/themes/bold-eco-v1/components/navbar/StandardHeader";
import { StoreNavigation01, StoreNavigation01DefaultProps, StoreNavigation01Schema } from "@/themes/bold-eco-v1/components/navbar/StoreNavigation01";
import { StandardFooter } from "@/themes/bold-eco-v1/components/footer/StandardFooter";
import { ModernFooter } from "@/themes/bold-eco-v1/components/footer/ModernFooter";
import { StoreNavigation02 } from "@/themes/bold-eco-v1/components/navbar/StoreNavigation02";
const EcoBuyCard = dynamic(() => import('@/components/ecoBuilder/blocks/eco/EcoBuyCard').then(m => m.EcoBuyCard));
const ExpressOrderForm = dynamic(() => import('@/components/ecoBuilder/blocks/eco/ExpressOrderForm').then(m => m.ExpressOrderForm));
const StickyBottomBar = dynamic(() => import('@/components/ecoBuilder/blocks/eco/StickyBottomBar').then(m => m.StickyBottomBar));

import { EcoBuyCardDefaultProps, EcoBuyCardSchema } from "@/components/ecoBuilder/blocks/eco/EcoBuyCard";
import { ExpressOrderFormDefaultProps, ExpressOrderFormSchema } from "@/components/ecoBuilder/blocks/eco/ExpressOrderForm";
import { StickyBottomBarDefaultProps, StickyBottomBarSchema } from "@/components/ecoBuilder/blocks/eco/StickyBottomBar";

const TrustBadges = dynamic(() => import('@/components/ecoBuilder/blocks/eco/TrustBadges').then(m => m.TrustBadges));
const BeforeAfterSlider = dynamic(() => import('@/components/ecoBuilder/blocks/eco/BeforeAfterSlider').then(m => m.BeforeAfterSlider));
const SocialHeaderFooter = dynamic(() => import('@/components/ecoBuilder/blocks/marketing/SocialHeaderFooter').then(m => m.SocialHeaderFooter));
const StickyWhatsApp = dynamic(() => import('@/components/ecoBuilder/blocks/eco/StickyWhatsApp').then(m => m.StickyWhatsApp));

import { TrustBadgesDefaultProps, TrustBadgesSchema } from "@/components/ecoBuilder/blocks/eco/TrustBadges";
import { BeforeAfterSliderDefaultProps, BeforeAfterSliderSchema } from "@/components/ecoBuilder/blocks/eco/BeforeAfterSlider";
import { SocialHeaderFooterDefaultProps, SocialHeaderFooterSchema } from "@/components/ecoBuilder/blocks/marketing/SocialHeaderFooter";
import { StickyWhatsAppDefaultProps, StickyWhatsAppSchema } from "@/components/ecoBuilder/blocks/eco/StickyWhatsApp";

// NEW SMART SECTIONS
import dynamic from 'next/dynamic';

const SmartCheckout = dynamic(() => import('@/components/ecoBuilder/blocks/marketing/SmartCheckout').then(m => m.SmartCheckout));
const StickyMobileBar = dynamic(() => import('@/components/ecoBuilder/blocks/marketing/StickyMobileBar').then(m => m.StickyMobileBar));
const TrustUrgency = dynamic(() => import('@/components/ecoBuilder/blocks/marketing/TrustUrgency').then(m => m.TrustUrgency));
const SocialProofReviews = dynamic(() => import('@/components/ecoBuilder/blocks/marketing/SocialProofReviews').then(m => m.SocialProofReviews), { ssr: false });
const ObjectionKillerFAQ = dynamic(() => import('@/components/ecoBuilder/blocks/marketing/ObjectionKillerFAQ').then(m => m.ObjectionKillerFAQ));
const ImageSection = dynamic(() => import('@/components/ecoBuilder/blocks/marketing/ImageSection').then(m => m.ImageSection));
const GallerySection = dynamic(() => import('@/components/ecoBuilder/blocks/marketing/GallerySection').then(m => m.GallerySection));
const SmartAnnouncementBar = dynamic(() => import('@/components/ecoBuilder/blocks/marketing/SmartAnnouncementBar').then(m => m.SmartAnnouncementBar));
const ShoppableLookbook = dynamic(() => import('@/components/ecoBuilder/blocks/marketing/ShoppableLookbook').then(m => m.ShoppableLookbook));

import { SmartCheckoutDefaultProps, SmartCheckoutSchema } from "@/components/ecoBuilder/blocks/marketing/SmartCheckout";
import { StickyMobileBarDefaultProps, StickyMobileBarSchema } from "@/components/ecoBuilder/blocks/marketing/StickyMobileBar";
import { TrustUrgencyDefaultProps, TrustUrgencySchema } from "@/components/ecoBuilder/blocks/marketing/TrustUrgency";
import { SocialProofReviewsDefaultProps, SocialProofReviewsSchema } from "@/components/ecoBuilder/blocks/marketing/SocialProofReviews";
import { ObjectionKillerFAQDefaultProps, ObjectionKillerFAQSchema } from "@/components/ecoBuilder/blocks/marketing/ObjectionKillerFAQ";
import { ImageSectionDefaultProps, ImageSectionSchema } from "@/components/ecoBuilder/blocks/marketing/ImageSection";
import { GallerySectionDefaultProps, GallerySectionSchema } from "@/components/ecoBuilder/blocks/marketing/GallerySection";
import { SmartAnnouncementBarDefaultProps, SmartAnnouncementBarSchema } from "@/components/ecoBuilder/blocks/marketing/SmartAnnouncementBar";
import { ShoppableLookbookDefaultProps, ShoppableLookbookSchema } from "@/components/ecoBuilder/blocks/marketing/ShoppableLookbook";

// Helper to normalize schema (Object -> Array)
function toEditorFields(schema: any): EditorField[] {
    if (Array.isArray(schema)) return schema;
    if (typeof schema === 'object' && schema !== null) {
        return Object.entries(schema).map(([key, field]: [string, any]) => ({
            key,
            ...field
        }));
    }
    return [];
}

export const COMPONENT_REGISTRY: Record<string, ComponentRegistryEntry> = {
    // ═══════════════════════════════════════════════════════════════
    // HEADERS (Legacy - Disabled)
    // ═══════════════════════════════════════════════════════════════
    /*
    standard_header: {
        category: "layout",
        component: StandardHeader as unknown as ComponentType<Record<string, unknown>>,
        label: "الهيدر القياسي (Standard)",
        icon: "layout-template",
        description: "الشريط العلوي الافتراضي مع الشعار والقائمة والبحث.",
        defaultProps: StandardHeaderDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(StandardHeaderSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    store_navigation_01: {
        category: "layout",
        component: StoreNavigation01 as unknown as ComponentType<Record<string, unknown>>,
        label: "هيدر عصري (Modern)",
        icon: "panel-top",
        description: "تصميم متجر عصري مع قائمة مركزية وشريط إعلاني.",
        defaultProps: StoreNavigation01DefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(StoreNavigation01Schema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    store_navigation_02: {
        category: "header",
        component: StoreNavigation02 as unknown as ComponentType<Record<string, unknown>>,
        label: "قائمة متجر حديثة (02)",
        icon: "panel-top-open",
        description: "شريط تنقل متقدم مع قوائم ضخمة (Mega Menu).",
        defaultProps: {} as unknown as Record<string, unknown>,
        editorSchema: [
            ...ADVANCED_SECTION_FIELDS
        ]
    },

    */

    // ═══════════════════════════════════════════════════════════════
    // FOOTERS
    // ═══════════════════════════════════════════════════════════════
    standard_footer: {
        category: "layout",
        component: StandardFooter as unknown as ComponentType<Record<string, unknown>>,
        label: "الفوتر القياسي (Standard)",
        icon: "layout-template",
        description: "التذييل الافتراضي مع القوائم وروابط التواصل.",
        defaultProps: {} as unknown as Record<string, unknown>,
        editorSchema: [
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    modern_footer: {
        category: "layout",
        component: ModernFooter as unknown as ComponentType<Record<string, unknown>>,
        label: "فوتر عصري (Modern)",
        icon: "panel-bottom",
        description: "تذييل بتصميم حديث مع أعمدة متعددة.",
        defaultProps: {} as unknown as Record<string, unknown>,
        editorSchema: [
            ...ADVANCED_SECTION_FIELDS
        ]
    },


    // 🌟 NEW HIGH CONVERSION COMPONENTS 🌟
    smart_checkout: {
        category: "product",
        component: SmartCheckout as unknown as ComponentType<Record<string, unknown>>,
        label: "🔥 نموذج الدفع الذكي (Smart Checkout)",
        icon: "shopping-bag",
        description: "أقوى نموذج لزيادة المبيعات مع دعم الدفع عند الاستلام وحساب التوصيل.",
        defaultProps: SmartCheckoutDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(SmartCheckoutSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    social_proof_reviews: {
        category: "marketing",
        component: SocialProofReviews as unknown as ComponentType<Record<string, unknown>>,
        label: "💬 آراء العملاء (Social Proof)",
        icon: "message-circle",
        description: "عرض التقييمات بطريقة واتساب أو بطاقات لزيادة الثقة.",
        defaultProps: SocialProofReviewsDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(SocialProofReviewsSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    objection_killer_faq: {
        category: "marketing",
        component: ObjectionKillerFAQ as unknown as ComponentType<Record<string, unknown>>,
        label: "❓ الأسئلة القاتلة للتردد (FAQ)",
        icon: "help-circle",
        description: "إجابة الاعتراضات (الشحن، الضمان) لإغلاق البيع.",
        defaultProps: ObjectionKillerFAQDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(ObjectionKillerFAQSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    before_after_slider: {
        category: "marketing",
        component: BeforeAfterSlider as unknown as ComponentType<Record<string, unknown>>,
        label: "↔️ المقارنة قبل/بعد (Slider)",
        icon: "monitor-play",
        description: "شريط تفاعلي للمقارنة بين صورتين (Before/After).",
        defaultProps: BeforeAfterSliderDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(BeforeAfterSliderSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    smart_sticky_bar: {
        category: "marketing",
        component: StickyMobileBar as unknown as ComponentType<Record<string, unknown>>,
        label: "📱 شريط الجوال العائم (Sticky Bar)",
        icon: "smartphone",
        description: "شريط يظهر عند التمرير في الجوال لزيادة نسبة النقر.",
        defaultProps: StickyMobileBarDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(StickyMobileBarSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    smart_trust_urgency: {
        category: "marketing",
        component: TrustUrgency as unknown as ComponentType<Record<string, unknown>>,
        label: "🛡️ شريط الثقة والاستعجال",
        icon: "shield-alert",
        description: "عداد تنازلي وشارات ثقة لتحفيز العميل على الشراء فوراً.",
        defaultProps: TrustUrgencyDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(TrustUrgencySchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },

    smart_announcement_bar: {
        category: "marketing",
        component: SmartAnnouncementBar as unknown as ComponentType<Record<string, unknown>>,
        label: "📣 شريط إعلاني مرن (Smart Bar)",
        icon: "megaphone",
        description: "شريط علوي متعدد الأنماط: عداد، سلايدر، زاحف، أو ثابت.",
        defaultProps: SmartAnnouncementBarDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(SmartAnnouncementBarSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },

    // ... (Keep existing entries below)
    eco_page_content: {
        // ...
        category: "basic",
        component: EcoPageContent as unknown as ComponentType<Record<string, unknown>>,
        label: "محتوى الصفحة الديناميكي",
        icon: "file-text",
        description: "يعرض محتوى HTML الأساسي لصفحة مختارة.",
        defaultProps: {
            pageId: "",
            container_width: "default",
            padding_y: "medium",
            show_title: true
        } as unknown as Record<string, unknown>,
        editorSchema: [
            {
                key: "pageId",
                label: "اختر الصفحة",
                type: "page", // This corresponds to the PagePicker
                description: "اختر الصفحة التي تريد عرض محتواها."
            },
            {
                key: "container_width",
                label: "عرض المحتوى",
                type: "select",
                options: [
                    { value: "default", label: "افتراضي (مقيد)" },
                    { value: "wide", label: "عريض (Wide)" },
                    { value: "full", label: "كامل (Full Width)" }
                ]
            },
            {
                key: "show_title",
                label: "عرض عنوان الصفحة",
                type: "boolean"
            },
            ...ADVANCED_SECTION_FIELDS
        ]
    },

    // ─────────────────────────────────────────────────────────────
    // PRODUCTS & COMMERCE
    // ─────────────────────────────────────────────────────────────
    eco_buy_card: {
        category: "product",
        component: EcoBuyCard as unknown as ComponentType<Record<string, unknown>>,
        label: "بطاقة شراء مباشرة",
        icon: "credit-card",
        description: "قسم شراء سريع لمنتج واحد.",
        defaultProps: EcoBuyCardDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(EcoBuyCardSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    express_order_form: {
        category: "product",
        component: ExpressOrderForm as unknown as ComponentType<Record<string, unknown>>,
        label: "نموذج الطلب السريع (COD)",
        icon: "clipboard-check",
        description: "نموذج طلب مباشر مع دعم الدفع عند الاستلام.",
        defaultProps: ExpressOrderFormDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(ExpressOrderFormSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    sticky_bottom_bar: {
        category: "marketing",
        component: StickyBottomBar as unknown as ComponentType<Record<string, unknown>>,
        label: "شريط الشراء العائم (Mobile)",
        icon: "arrow-down-to-line",
        description: "شريط سفلي يظهر فقط في الهاتف لتسهيل الشراء.",
        defaultProps: StickyBottomBarDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(StickyBottomBarSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    sticky_whatsapp: {
        category: "marketing",
        component: StickyWhatsApp as unknown as ComponentType<Record<string, unknown>>,
        label: "رمز واتساب العائم",
        icon: "message-circle",
        description: "أيقونة عائمة للتواصل المباشر مع دعم رسائل ترحيب.",
        defaultProps: StickyWhatsAppDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(StickyWhatsAppSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    social_header_footer: {
        category: "marketing",
        component: SocialHeaderFooter as unknown as ComponentType<Record<string, unknown>>,
        label: "شريط التواصل الاجتماعي",
        icon: "share-2",
        description: "شريط يجمع بين الروابط الاجتماعية والشعار (نافبار/فوتر).",
        defaultProps: SocialHeaderFooterDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(SocialHeaderFooterSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    shoppable_lookbook: {
        category: "marketing",
        component: ShoppableLookbook as unknown as ComponentType<Record<string, unknown>>,
        label: "📸 الصور القابلة للتسوق (Lookbook)",
        icon: "camera",
        description: "صورة تفاعلية مع نقاط شراء (Hotspots) للمنتجات المعروضة.",
        defaultProps: ShoppableLookbookDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(ShoppableLookbookSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },

    trust_badges: {
        category: "marketing",
        component: TrustBadges as unknown as ComponentType<Record<string, unknown>>,
        label: "شارات الثقة والضمان",
        icon: "shield-check",
        description: "مجموعة أيقونات لزيادة الثقة (شحن، دفع، ضمان).",
        defaultProps: TrustBadgesDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(TrustBadgesSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },

    // ─────────────────────────────────────────────────────────────
    // ATOMS / BASICS (Maintenance for Advanced Mode)
    // ─────────────────────────────────────────────────────────────
    container: {
        category: "layout",
        component: Container as unknown as ComponentType<Record<string, unknown>>,
        label: "صندوق / حاوية",
        icon: "box",
        description: "حاوية تستخدم لتجميع العناصر وضبط الهوامش والخلفية.",
        defaultProps: {
            size: "lg",
            padding_y: "medium"
        } as unknown as Record<string, unknown>,
        editorSchema: [
            {
                key: "size",
                label: "عرض الحاوية",
                type: "select",
                options: [
                    { value: "sm", label: "صغير (Small)" },
                    { value: "md", label: "متوسط (Medium)" },
                    { value: "lg", label: "كبير (Large)" },
                    { value: "xl", label: "كبير جداً (XL)" },
                    { value: "full", label: "كامل (Full Width)" }
                ]
            },
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    text: {
        category: "basic",
        component: TextAtom as unknown as ComponentType<Record<string, unknown>>,
        label: "نص",
        icon: "type",
        description: "عنصر نصي قابل للتعديل.",
        defaultProps: TextAtomDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(TextAtomSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    image_section: {
        category: "basic", // Or marketing
        component: ImageSection as unknown as ComponentType<Record<string, unknown>>,
        label: "نص مع صورة خلفية",
        icon: "image",
        description: "قسم كامل العرض مع صورة وعنوان.",
        defaultProps: ImageSectionDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(ImageSectionSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    gallery_section: {
        category: "basic",
        component: GallerySection as unknown as ComponentType<Record<string, unknown>>,
        label: "معرض صور",
        icon: "gallery-horizontal",
        description: "قسم معرض صور متحرك (Slider).",
        defaultProps: GallerySectionDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(GallerySectionSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    },
    button: {
        category: "basic",
        component: ButtonAtom as unknown as ComponentType<Record<string, unknown>>,
        label: "زر",
        icon: "mouse-pointer-click",
        description: "زر قابل للنقر.",
        defaultProps: ButtonAtomDefaultProps as unknown as Record<string, unknown>,
        editorSchema: [
            ...toEditorFields(ButtonAtomSchema),
            ...ADVANCED_SECTION_FIELDS
        ]
    }
};

export function getComponentByType(type: string) {
    return COMPONENT_REGISTRY[type] ? COMPONENT_REGISTRY[type].component : null;
}

export function getComponentLabel(type: string) {
    return COMPONENT_REGISTRY[type] ? COMPONENT_REGISTRY[type].label : type;
}

export function getDefaultProps(type: string) {
    return COMPONENT_REGISTRY[type] ? COMPONENT_REGISTRY[type].defaultProps : {};
}

export function getEditorSchema(type: string) {
    return COMPONENT_REGISTRY[type] ? COMPONENT_REGISTRY[type].editorSchema : [];
}

